// src/pages/Home.js
// The signed-in home surface. Four sections, in order:
//
//   Continue editing  — Studio projects        (Studio's own API, own session)
//   Your images       — GET /drops?type=image  (Mongo `drops`)
//   Your videos       — GET /drops?type=video  (Mongo `videos`, completed only)
//   Start from a free clip — Pexels + Pixabay, proxied by our backend
//
// No Drift content appears here. Drift is other people's AI work, remixable,
// with a recipe and creator credits attached; it keeps its own destination.
//
// "Recreate" routes by media type: an image opens the Image Studio, a video
// the Video Studio. It reuses App.js's existing startRemix path rather than a
// parallel one, so a drop prefills from its own recreate_payload exactly as a
// Drift remix does.

import { useCallback, useEffect, useState } from "react";
import { drops as dropsApi, freeclips as freeclipsApi } from "../api";
import { listProjects, studioProjectUrl, studioNewProjectUrl, StudioError } from "../lib/studio";
import MediaViewer from "../components/MediaViewer";

const PROJECT_LIMIT = 6;
const DROPS_PAGE = 50; // one call, split client-side — see loadDrops below
const ROW = 5;

const CATEGORY_LABELS = {
  all: "All",
  portraits: "Portraits",
  urban: "Urban",
  nature: "Nature",
  abstract: "Abstract",
  product: "Product",
  aerial: "Aerial",
};

/* ---------------------------------------------------------------- helpers */

function editedAt(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// A video drop's thumbnail_url falls back through thumbnail_url -> image_url
// -> video_url on the backend, so it can end up being the mp4 itself. Pointing
// an <img> at that renders broken, so detect it and use a <video> poster frame.
const isPlayable = (d) => d.type === "video" && d.thumbnail_url && d.thumbnail_url === d.url;

const RecreateIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

function SectionHead({ title, note, action }) {
  return (
    <div className="home-section__head">
      <div className="home-section__title">
        <h2>{title}</h2>
        {note && <span>{note}</span>}
      </div>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------ cards */

function MediaTile({ item, badge, creator, busy, onRecreate, onOpen }) {
  return (
    <div className="mcard home-tile" onClick={onOpen}>
      <div className="mcard__media">
        {isPlayable(item) ? (
          <video src={item.url} muted playsInline preload="metadata" />
        ) : (
          <img src={item.thumbnail_url} alt={item.prompt || ""} loading="lazy" />
        )}

        {badge && <span className="mcard__badge">{badge}</span>}

        <div className="mcard__overlay">
          <span className="mcard__creator" title={creator || ""}>
            <span>{creator || ""}</span>
          </span>
          <button
            className="mcard__remix"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              onRecreate();
            }}
          >
            {busy ? "Opening…" : <><RecreateIcon /> Recreate</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Skeletons({ count = ROW, height = 150 }) {
  return (
    <div className="home-row" aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <div className="mcard mcard--skeleton" key={i} style={{ height }} />
      ))}
    </div>
  );
}

function Empty({ icon, title, body, cta }) {
  return (
    <div className="home-empty">
      <span className="home-empty__icon">{icon}</span>
      <div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
      {cta}
    </div>
  );
}

/* ------------------------------------------------------------------- page */

export default function Home({ onRecreate, onNavigate }) {
  // Continue editing (Studio)
  const [projects, setProjects] = useState(null);
  const [projectsFailed, setProjectsFailed] = useState(false);

  // Your images / Your videos (one /drops call, split by type)
  const [myDrops, setMyDrops] = useState(null);
  const [dropsFailed, setDropsFailed] = useState(false);

  // Free clips
  const [category, setCategory] = useState("all");
  const [clips, setClips] = useState(null);
  const [clipsFailed, setClipsFailed] = useState(false);
  const [importing, setImporting] = useState(null);

  // Which list is open in the full-size viewer, and where in it.
  const [viewer, setViewer] = useState(null); // { items, index }

  const loadProjects = useCallback(() => {
    setProjectsFailed(false);
    listProjects({ limit: PROJECT_LIMIT })
      .then((data) => setProjects(data.projects || []))
      .catch((error) => {
        // No Studio configured, or not signed in there yet: nothing is wrong
        // and nothing to retry — show the invitation, not an error.
        setProjects([]);
        if (!(error instanceof StudioError) || error.kind === "failed") setProjectsFailed(true);
      });
  }, []);

  // One untyped call rather than two typed ones: /drops paginates in Python,
  // pulling up to 500 images plus 500 videos and merging them per request, so
  // each extra call repeats that whole merge. This also hits the page-1 cache.
  const loadDrops = useCallback(() => {
    setDropsFailed(false);
    dropsApi
      .list({ limit: DROPS_PAGE })
      .then((data) => setMyDrops(data.drops || []))
      .catch(() => {
        setMyDrops([]);
        setDropsFailed(true);
      });
  }, []);

  const loadClips = useCallback((cat) => {
    setClips(null);
    setClipsFailed(false);
    freeclipsApi
      .list({ category: cat, limit: 12 })
      .then((data) => setClips(data.items || []))
      .catch(() => {
        setClips([]);
        setClipsFailed(true);
      });
  }, []);

  useEffect(loadProjects, [loadProjects]);
  useEffect(loadDrops, [loadDrops]);
  useEffect(() => loadClips(category), [loadClips, category]);

  const images = (myDrops || []).filter((d) => d.type === "image").slice(0, ROW);
  const videos = (myDrops || []).filter((d) => d.type === "video").slice(0, ROW);

  // A free clip has no recreate_payload — it is not a Karvi generation. Copy it
  // into our own storage first (the provider forbids permanent hotlinking, and
  // the studio needs a URL it can rely on), then hand the studio a seed shaped
  // like the remix objects it already understands.
  const recreateClip = async (clip) => {
    const key = `${clip.provider}:${clip.asset_id}`;
    setImporting(key);
    try {
      const { url } = await freeclipsApi.importClip({
        provider: clip.provider,
        asset_id: clip.asset_id,
        kind: clip.kind,
      });
      // Shape the seed the way each studio actually reads it — the field names
      // differ, and a mismatch is silent: the studio opens with nothing in it.
      // CreateVideo animates a still in "motion" mode via image_url;
      // CreateImage guides from reference_image_url when generation_type is refx.
      onRecreate(
        clip.kind === "video"
          ? { type: "video", prompt: clip.prompt || "", generation_mode: "motion", image_url: url }
          : { type: "image", prompt: clip.prompt || "", generation_type: "refx", reference_image_url: url }
      );
    } catch {
      setClipsFailed(true);
    } finally {
      setImporting(null);
    }
  };

  return (
    <div className="page home">
      <div className="home__head">
        <h1>Welcome back</h1>
        <p className="page__sub">Pick up where you left off, or start from something new.</p>
      </div>

      {/* ---------------- Continue editing ---------------- */}
      <section className="home-section">
        <SectionHead
          title="Continue editing"
          note={projects && projects.length > 0 ? "Newest edit first" : null}
          action={
            projects && projects.length > 0 ? (
              <a className="home-section__link" href={studioNewProjectUrl()}>All projects →</a>
            ) : null
          }
        />

        {projects === null && (
          <div className="pcard-row" aria-busy="true">
            {Array.from({ length: PROJECT_LIMIT }).map((_, i) => (
              <div className="pcard pcard--skeleton" key={i}>
                <span className="pcard__thumb" />
                <span className="pcard__body"><b /><em /></span>
              </div>
            ))}
          </div>
        )}

        {projects !== null && projectsFailed && (
          <div className="home-retry">
            <p>Could not load your projects just now.</p>
            <button className="btn btn--ghost" onClick={loadProjects}>Try again</button>
          </div>
        )}

        {projects !== null && !projectsFailed && projects.length === 0 && (
          <Empty
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M12 10v5" /><path d="M9.5 12.5h5" /></svg>
            }
            title="Start a project"
            body="Studio is where you cut clips together, add voice and music, and export the finished thing. Nothing here yet."
            cta={<a className="btn btn--primary" href={studioNewProjectUrl()}>Open Studio →</a>}
          />
        )}

        {projects !== null && !projectsFailed && projects.length > 0 && (
          <div className="pcard-row">
            {projects.map((p) => (
              <a className="pcard" href={studioProjectUrl(p.id)} key={p.id}>
                <span
                  className="pcard__thumb"
                  style={p.thumbnail_url ? { backgroundImage: `url(${p.thumbnail_url})` } : undefined}
                >
                  {!p.thumbnail_url && (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 10h18" /><path d="M8 5v5" /></svg>
                  )}
                </span>
                <span className="pcard__body">
                  <b>{p.name || "Untitled project"}</b>
                  <em>{editedAt(p.updated_at)}</em>
                </span>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* ---------------- Your images ---------------- */}
      <section className="home-section">
        <SectionHead
          title="Your images"
          note={images.length > 0 ? "Recreate opens the Image Studio" : null}
          action={<button className="home-section__link" onClick={() => onNavigate("drops")}>My drops →</button>}
        />

        {myDrops === null && <Skeletons />}

        {myDrops !== null && dropsFailed && (
          <div className="home-retry">
            <p>Could not load your generations just now.</p>
            <button className="btn btn--ghost" onClick={loadDrops}>Try again</button>
          </div>
        )}

        {myDrops !== null && !dropsFailed && images.length === 0 && (
          <Empty
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="8.5" cy="9.5" r="1.6" /><path d="m4 17 5-5 4 4 3-2 4 4" /></svg>}
            title="No images yet"
            body="Generate your first image and it will show up here, ready to restyle or remix."
            cta={<button className="btn btn--primary" onClick={() => onNavigate("image")}>Create an image →</button>}
          />
        )}

        {myDrops !== null && !dropsFailed && images.length > 0 && (
          <div className="home-row">
            {images.map((d) => (
              <MediaTile
                key={d.drop_id}
                item={d}
                badge="IMAGE"
                creator={editedAt(d.created_at)}
                onRecreate={() => onRecreate(d)}
                onOpen={() => setViewer({ items: images, index: images.indexOf(d) })}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---------------- Your videos ---------------- */}
      <section className="home-section">
        <SectionHead
          title="Your videos"
          note={videos.length > 0 ? "Recreate opens the Video Studio" : null}
          action={<button className="home-section__link" onClick={() => onNavigate("drops")}>My drops →</button>}
        />

        {myDrops === null && <Skeletons />}

        {myDrops !== null && !dropsFailed && videos.length === 0 && (
          <Empty
            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 10h18" /><path d="M8 5v5" /><path d="M16 5v5" /></svg>}
            title="No videos yet"
            body="Direct a shot from a single prompt, or animate one of your images into motion."
            cta={<button className="btn btn--primary" onClick={() => onNavigate("video")}>Create a video →</button>}
          />
        )}

        {myDrops !== null && !dropsFailed && videos.length > 0 && (
          <div className="home-row">
            {videos.map((d) => (
              <MediaTile
                key={d.drop_id}
                item={d}
                badge={d.duration ? `▶ 0:${String(d.duration).padStart(2, "0")}` : "▶ video"}
                creator={editedAt(d.created_at)}
                onRecreate={() => onRecreate(d)}
                onOpen={() => setViewer({ items: videos, index: videos.indexOf(d) })}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---------------- Free clips ---------------- */}
      <section className="home-section">
        <SectionHead
          title="Start from a free clip"
          note="Trending this week · costs no credits"
        />

        <div className="chips home-chips">
          {Object.keys(CATEGORY_LABELS).map((key) => (
            <button
              key={key}
              className={`chip ${category === key ? "chip--active" : ""}`}
              onClick={() => setCategory(key)}
            >
              {CATEGORY_LABELS[key]}
            </button>
          ))}
        </div>

        {clips === null && <Skeletons count={6} height={190} />}

        {clips !== null && clipsFailed && clips.length === 0 && (
          <div className="home-retry">
            <p>Could not load free clips just now.</p>
            <button className="btn btn--ghost" onClick={() => loadClips(category)}>Try again</button>
          </div>
        )}

        {clips !== null && !clipsFailed && clips.length > 0 && (
          <>
            <div className="home-row home-row--six">
              {clips.slice(0, 12).map((c) => (
                <MediaTile
                  key={`${c.provider}:${c.asset_id}`}
                  item={{ ...c, url: c.thumbnail_url }}
                  badge={
                    c.kind === "video"
                      ? `▶ 0:${String(c.duration || 0).padStart(2, "0")}`
                      : "IMAGE"
                  }
                  creator={c.creator}
                  busy={importing === `${c.provider}:${c.asset_id}`}
                  onRecreate={() => recreateClip(c)}
                  onOpen={() => setViewer({ items: clips, index: clips.indexOf(c) })}
                />
              ))}
            </div>
            {/* Required by both providers' API terms whenever their media is shown. */}
            <span className="home-credit">Free clips provided by Pexels and Pixabay</span>
          </>
        )}
      </section>

      {viewer && (
        <MediaViewer
          items={viewer.items}
          index={viewer.index}
          onNav={(index) => setViewer((v) => ({ ...v, index }))}
          onClose={() => setViewer(null)}
          onRecreate={(item) => {
            setViewer(null);
            if (item.provider) recreateClip(item);
            else onRecreate(item);
          }}
        />
      )}
    </div>
  );
}
